/**
 * Dialog para preview e envio de vídeo
 * 
 * Permite visualizar vídeo selecionado antes de enviar,
 * adicionar legenda opcional e validar tamanho do arquivo.
 * Limite de 20MB.
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Video, X } from 'lucide-react';
import { EmojiPicker } from './EmojiPicker';

interface SendVideoDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
  /** Arquivo de vídeo selecionado */
  videoFile: File | null;
  /** Callback para enviar vídeo com legenda opcional */
  onSend: (file: File, caption?: string) => Promise<void>;
}

// Limite de 20MB para vídeos
const MAX_VIDEO_SIZE = 20 * 1024 * 1024;

export function SendVideoDialog({
  open,
  onClose,
  videoFile,
  onSend,
}: SendVideoDialogProps) {
  // Estados do componente
  const [caption, setCaption] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  
  // Ref para o elemento de vídeo (para capturar duração)
  const videoRef = useRef<HTMLVideoElement>(null);

  // Gera preview URL e valida arquivo quando muda
  useEffect(() => {
    if (!videoFile) {
      setPreviewUrl(null);
      setError(null);
      setDuration(null);
      return;
    }

    // Valida tamanho (máx. 20MB)
    if (videoFile.size > MAX_VIDEO_SIZE) {
      setError(`Vídeo muito grande (${formatFileSize(videoFile.size)}). O limite é 20MB.`);
      return;
    }

    setError(null);
    const url = URL.createObjectURL(videoFile);
    setPreviewUrl(url);

    // Cleanup: revoga URL ao desmontar
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [videoFile]);

  // Limpa estados quando dialog fecha
  useEffect(() => {
    if (!open) {
      setCaption('');
      setIsSending(false);
      setPreviewUrl(null);
      setError(null);
      setDuration(null);
    }
  }, [open]);

  // Handler para metadados carregados (captura duração)
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  // Handler para enviar vídeo (fire-and-forget): fecha modal já,
  // chat trata otimismo/erros via toast e status='failed' no balão.
  const handleSend = () => {
    if (!videoFile || error) return;
    const fileToSend = videoFile;
    const captionToSend = caption.trim() || undefined;
    onClose();
    Promise.resolve(onSend(fileToSend, captionToSend)).catch((err) => {
      console.error('[SendVideoDialog] background send error:', err);
    });
  };

  // Handler para Enter no input de legenda
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSending) {
      e.preventDefault();
      handleSend();
    }
  };

  // Formata tamanho do arquivo em KB ou MB
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Formata duração em mm:ss
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Enviar vídeo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview do vídeo */}
          {previewUrl && !error ? (
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={previewUrl}
                controls
                className="w-full h-full object-contain"
                onLoadedMetadata={handleLoadedMetadata}
              />
            </div>
          ) : (
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
              {error ? (
                <div className="text-center p-4">
                  <X className="h-8 w-8 text-destructive mx-auto mb-2" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              ) : (
                <Video className="h-12 w-12 text-muted-foreground" />
              )}
            </div>
          )}

          {/* Informações do arquivo */}
          {videoFile && !error && (
            <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/50 rounded px-3 py-2">
              <span className="truncate max-w-[200px]" title={videoFile.name}>
                {videoFile.name}
              </span>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                {duration && <span>{formatDuration(duration)}</span>}
                <span>{formatFileSize(videoFile.size)}</span>
              </div>
            </div>
          )}

          {/* Textarea de legenda com EmojiPicker: Enter envia, Shift+Enter quebra linha (igual ao chat) */}
          <div className="relative">
            <Textarea
              placeholder="Adicione uma legenda (opcional) — Shift+Enter para nova linha"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending || !!error}
              rows={2}
              className="resize-none min-h-[40px] max-h-[120px] pr-10"
            />
            {/* Botão de emoji: append simples ao final da legenda */}
            <EmojiPicker
              onSelect={(emoji) => setCaption((prev) => prev + emoji)}
              disabled={isSending || !!error}
              className="absolute bottom-2 right-2"
            />
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSend}
              disabled={!videoFile || isSending || !!error}
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
